import { faArrowLeft, faArrowRight, faScanner } from '@fortawesome/pro-regular-svg-icons';
import Button from '@ssg/common/Components/Button';
import Modal from '@ssg/common/Components/Modal';
import React from 'react';
import BarcodeScanner from '../Movables/BarcodeScannerTwo';
import { useTranslation } from 'react-i18next';
import SearchableSelect from '@ssg/common/Components/SearchableSelect';
import useDebouncedState from '@ssg/common/Hooks/useDebouncedState';
import { SelectOption } from '@ssg/common/Helpers/Helpers';
import { useForm } from 'react-hook-form';
import { useMoveWebMachinesMutation } from '@ssg/common/GraphQL/indexV2';

interface ScannerProps {
	show: boolean;
	type: 'machines' | 'location';
}
interface MoveMachinesProps {
	machineLocations: SelectOption[];
}

const MoveMachines: React.FC<MoveMachinesProps> = ({ machineLocations }): React.ReactElement => {
	const { t } = useTranslation();
	const [showScannerModalMultiple, setShowScannerModalMultiple] = React.useState<ScannerProps | null>(null);
	const [scannedId, setScannedId] = React.useState<string | undefined>(undefined);
	const scannedIdListRef = React.useRef<string[] | null>(null);
	const [machineLocationSearchText, setMachineLocationSearchText] = useDebouncedState('', 100);
	const [selectedLocation, setSelectedLocation] = React.useState<string | undefined>(undefined);

	const dummyForm = useForm();

	const loading = false; // TODO: Add loading state when fetching machine data

	const [moveMachines, { loading: moveMachinesLoading }] = useMoveWebMachinesMutation();

	const submitChangeLocation = async () => {
		if (!selectedLocation || !scannedIdListRef.current) return;
		await moveMachines({
			variables: {
				erpReferenceNumbers: scannedIdListRef.current,
				erpLocationReference: selectedLocation,
				erpCaseReference: '',
			},
		});
		setShowScannerModalMultiple(null);
		scannedIdListRef.current = null;
		setSelectedLocation(undefined);
	};

	return (
		<>
			<Button
				primary
				text="machines.changeLocations"
				icon={faScanner}
				onClick={() => {
					setScannedId(undefined);
					setShowScannerModalMultiple({
						show: true,
						type: 'machines',
					});
				}}
				className="py-5"
				textClassName="text-xl ml-5"
				fullWidth
				iconSize="lg"
				loading={loading}
				loadingColor="text-blue"
				disabled={loading}
			/>

			<Modal
				title="common.scanMultipleLabels"
				close={() => setShowScannerModalMultiple(null)}
				visible={showScannerModalMultiple?.show ?? false}
				body={
					showScannerModalMultiple?.type === 'machines' ? (
						<BarcodeScanner setScannedId={setScannedId} multiple />
					) : (
						<div className="h-48">
							<SearchableSelect
								control={dummyForm.control}
								name="erpLocationReference"
								title="common.location"
								required
								options={machineLocations.filter(l => l.label.toLowerCase().includes(machineLocationSearchText.toLowerCase()))}
								searchFn={searchText => setMachineLocationSearchText(searchText)}
								onSelect={value => {
									setSelectedLocation(value);
								}}
								onBlur={() => undefined}
								minInputLength={2}
								isLoading={false}
							/>
						</div>
					)
				}
				footer={
					<div className="flex w-full flex-row justify-between">
						{showScannerModalMultiple?.type === 'machines' ? (
							<>
								<span className="self-center">
									{scannedId?.split(',').length ?? 0} {t('common.scannedItems')}
								</span>
								<Button
									primary
									backIcon={faArrowRight}
									text="machines.selectLocation"
									onClick={() => {
										scannedIdListRef.current = scannedId?.split(',') ?? [];
										setScannedId(undefined);
										setShowScannerModalMultiple({
											show: true,
											type: 'location',
										});
									}}
									disabled={(scannedId?.split(',').length ?? 0) === 0}
								/>
							</>
						) : (
							<>
								<span className="self-center">
									{scannedIdListRef.current?.length ?? 0} {t('common.scannedItems')}
								</span>
								<Button
									success
									text="machines.changeLocation"
									onClick={() => {
										submitChangeLocation();
									}}
									disabled={!selectedLocation}
									loading={moveMachinesLoading}
								/>
							</>
						)}
					</div>
				}
			/>
		</>
	);
};

export default MoveMachines;

